import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  useSubscriptionCategories,
  useUpdateSubscriptionCategories,
} from '../../hooks/useSubscriptions';
import type { SubscriptionCategoryDef } from '../../lib/subscriptionsApi';

// Settings → "Categorii abonamente". Editable nomenclator (code + label)
// stored in AppConfig; used by the subscription form/filters and the Excel
// import (which auto-creates missing categories). The whole list is saved at
// once via PUT /api/subscriptions/categories. The fallback category 'other'
// cannot be removed — the backend re-adds it anyway.

export function SubscriptionCategoriesSettings() {
  const { data, isLoading, isError } = useSubscriptionCategories();
  const updateMutation = useUpdateSubscriptionCategories();

  const [draft, setDraft] = useState<SubscriptionCategoryDef[] | null>(null);
  const [newLabel, setNewLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Re-sync the draft whenever fresh server data arrives and nothing is edited.
  useEffect(() => {
    if (data && draft === null) setDraft(data);
  }, [data, draft]);

  const categories = draft ?? data ?? [];
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(data ?? []);

  function patchLabel(index: number, label: string) {
    setSaved(false);
    setDraft((prev) => {
      const base = prev ?? data ?? [];
      return base.map((c, i) => (i === index ? { ...c, label } : c));
    });
  }

  function addCategory() {
    const label = newLabel.trim();
    if (!label) return;
    setSaved(false);
    setDraft((prev) => [...(prev ?? data ?? []), { code: '', label }]);
    setNewLabel('');
  }

  function removeCategory(index: number) {
    setSaved(false);
    setDraft((prev) => (prev ?? data ?? []).filter((_, i) => i !== index));
  }

  async function handleSave() {
    if (!draft) return;
    setError(null);
    setSaved(false);
    const cleaned = draft
      .map((c) => ({ ...c, label: c.label.trim() }))
      .filter((c) => c.label.length > 0);
    try {
      const result = await updateMutation.mutateAsync(cleaned);
      setDraft(result);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Salvarea nu a reușit.');
    }
  }

  return (
    <div className="max-w-2xl">
      <p className="text-sm text-gray-500">
        Categoriile folosite la abonamente (formular, filtre și importul Excel). La import,
        categoriile noi din fișier sunt adăugate automat în această listă.
      </p>

      <div className="mt-4 flex items-end gap-2">
        <label className="flex-1">
          <span className="mb-1 block text-xs font-medium text-gray-600">Categorie nouă</span>
          <input
            type="text"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addCategory();
              }
            }}
            placeholder="ex. AI Provider"
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </label>
        <button
          type="button"
          onClick={addCategory}
          disabled={!newLabel.trim()}
          className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          <Plus size={16} /> Adaugă
        </button>
      </div>

      {error && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="mt-4">
        {isLoading && <p className="text-sm text-gray-500">Se încarcă categoriile...</p>}
        {isError && <p className="text-sm text-red-600">Categoriile nu au putut fi încărcate.</p>}
        {!isLoading && !isError && (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Etichetă</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Cod</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Acțiuni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {categories.map((cat, index) => (
                  <tr key={cat.code || `new-${index}`} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={cat.label}
                        onChange={(e) => patchLabel(index, e.target.value)}
                        className="w-full rounded border border-transparent px-1 py-0.5 text-sm hover:border-gray-200 focus:border-brand-500 focus:outline-none"
                      />
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-500">
                      {cat.code || <span className="italic text-gray-400">generat la salvare</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {cat.code !== 'other' && (
                        <button
                          type="button"
                          onClick={() => removeCategory(index)}
                          title="Șterge"
                          className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {categories.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-3 py-4 text-center text-sm text-gray-500">
                      Nicio categorie. Adaugă una mai sus.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={!dirty || updateMutation.isPending}
          className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {updateMutation.isPending && (
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
          )}
          Salvează modificările
        </button>
        {saved && !dirty && <span className="text-sm text-green-600">Salvat.</span>}
        <p className="ml-auto text-xs text-gray-400">
          Redenumirea păstrează codul; abonamentele existente rămân asociate.
        </p>
      </div>
    </div>
  );
}
